import React from 'react';
import { Box, Skeleton, IconButton } from '@mui/material';

interface EvaluatingSkeletonItemProps {
  fileName: string;
}

/**
 * Shown in place of UploadFileItem / EvaluatedFileItem while the
 * evaluate API call is in-flight for a specific file.
 *
 * Figma spec:
 *  - File name visible at top (not a skeleton — user needs to see which file)
 *  - Two shimmer bars below:
 *      Row 1: spark icon  + wide bar  (~100% width)
 *      Row 2: spark icon  + narrow bar (~80% width, slightly lighter)
 *  - Trash icon disabled (grey) on the right
 */
const EvaluatingSkeletonItem: React.FC<EvaluatingSkeletonItemProps> = ({ fileName }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        py: 1.5,
        px: 1,
        backgroundColor: '#FBFBFB',
        borderRadius: '8px',
      }}
    >
      {/* ── Row 1: filename + disabled delete ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box
          title={fileName}
          sx={{
            fontSize: 15,
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 500,
            color: '#2C2E50',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {fileName}
        </Box>

        {/* Disabled trash — greyed out during evaluation */}
        <IconButton size="small" disabled sx={{ opacity: 0.35 }}>
          <img src="/images/trash-icon-blue.svg" alt="delete-file" width={14} height={14} />
        </IconButton>
      </Box>

      {/* ── Shimmer bar 1 (full width) ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <img
          src="/images/stars-new.svg"
          alt="stars-icon"
          width={14}
          height={14}
          style={{ flexShrink: 0, opacity: 0.6 }}
        />
        <Skeleton
          variant="rounded"
          height={8}
          sx={{
            flex: 1,
            borderRadius: '4px',
            backgroundColor: '#E3EAFF',
            '&::after': {
              background: 'linear-gradient(90deg, transparent, rgba(26,85,249,0.15), transparent)',
            },
          }}
        />
      </Box>

      {/* ── Shimmer bar 2 (narrower) ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 14, flexShrink: 0 }} /> {/* spacer to align with bar 1 */}
        <Skeleton
          variant="rounded"
          height={8}
          sx={{
            width: '75%',
            borderRadius: '4px',
            backgroundColor: '#EEF3FF',
            '&::after': {
              background: 'linear-gradient(90deg, transparent, rgba(26,85,249,0.10), transparent)',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default EvaluatingSkeletonItem;
